import React, { Component } from "react";
import Jumbotron from "../../components/Jumbotron";
import PageLayout from "../../components/PageLayout";
import {
  getCurrentSiteState,
  GlobalState,
} from "../../../redux/modules/rootReducer";
import NavBar from "../../NavBar/NavBar";

import { fetchCurrentSite } from "../../../redux/modules/currentSite/asyncActions";
import { connect } from "react-redux";
import {
  Customer,
  ensureCustomer,
  getCustomerById,
} from "../../../redux/modules/customers/reducer";
import { ensureSite } from "../../../redux/modules/sites/actions";
import { pushRoute } from "../../../redux/modules/route/actions";
import { loadWorkflows } from "../../../redux/modules/workflow/actions";
import { log as logToBws } from "../../../redux/modules/logger/actions";
import {
  Workflow,
  getWorkflowsList,
} from "../../../redux/modules/workflow/reducer";
import {
  Site,
  getSiteById,
  createCompositeSiteId,
} from "../../../redux/modules/sites/reducer";
import { CurrentSiteState } from "../../../redux/modules/currentSite/reducer";
import { AclState } from "../../../acl/reducer";
import { DashboardPageContent } from "./DashboardPageContent";
import { UserState } from "../../../redux/modules/user/reducer";
import { AnyAction } from "redux";
import { siteHasPermissions } from "../../../acl/utils";
import { ensureAcl } from "../../../acl/asyncActions";
import { EVENT_PILOT_APP_LINK_OPENED } from "../../../redux/modules/logger/constants";

type DashboardProps = {
  // own props
  siteId: string;
  customerId: string;

  // injected by connect
  site: Site;
  customer: Customer | null;
  user: UserState;
  currentSite: CurrentSiteState;
  siteCreationWorkflows: Workflow[];
  acl: AclState;

  // bound actions
  pushRoute: (url: string) => AnyAction;
  ensureCustomer: (customerId: string) => Promise<Customer | null>;
  ensureSite: (customerId: string, siteId: string) => Promise<Site>;
  logToBws: typeof logToBws;
  loadWorkflows: (
    customerId: string,
    siteId: string,
    workflowType: string,
  ) => Promise<void>;
  // ensureCurrentSite: (customerId: string, siteId: string) => Promise<Site>;
  fetchCurrentSite: (customerId: string, siteId: string) => Promise<Site>;
  ensureAcl: (customerId: string, siteId: string) => Promise<void>;
};

class DashboardContainer extends Component<DashboardProps> {
  constructor(props: DashboardProps) {
    super(props);
  }

  componentDidMount() {
    const {
      ensureCustomer,
      ensureSite,
      customerId,
      siteId,
      loadWorkflows,
      fetchCurrentSite,
      ensureAcl,
    } = this.props;

    if (!customerId) {
      return;
    }
    ensureAcl(customerId, siteId);
    ensureCustomer(customerId).then(() => {
      ensureSite(customerId, siteId);
    });
    loadWorkflows(customerId, siteId, "SITE_CREATION");
    fetchCurrentSite(customerId, siteId);
  }

  // Quick way to ensure we can trace who clicked on this way at least in bws logs
  private logPilotAppOpened() {
    const userEmail = this.props.user.email || "unknown email";
    const contextInfo = `${this.props.customerId}/${this.props.siteId}`;

    const isInternalUser = userEmail.endsWith("@traceair.net");
    const userOriginMessage = isInternalUser
      ? `by internal user ${userEmail}`
      : `by external user ${userEmail}`;
    this.props.logToBws(
      EVENT_PILOT_APP_LINK_OPENED,
      "1",
      `Pilot App link clicked at ${contextInfo} ${userOriginMessage}`,
    );
  }

  render() {
    const {
      pushRoute,
      siteCreationWorkflows,
      customerId,
      siteId,
      customer,
      acl,
      site,
      currentSite,
    } = this.props;
    return (
      <PageLayout
        navBar={<NavBar />}
        header={this.renderHeader()}
        content={
          <DashboardPageContent
            pushRoute={pushRoute}
            logPilotAppLinkClick={() => this.logPilotAppOpened()}
            siteCreationWorkflows={siteCreationWorkflows}
            customerId={customerId}
            siteId={siteId}
            customer={customer}
            acl={acl}
            site={site}
            currentSite={currentSite}
          />
        }
      />
    );
  }

  renderHeader() {
    const { site, customer, acl, customerId } = this.props;
    // TODO: move loading status to component state
    if (!site || !customer || !acl) {
      return <Jumbotron subheader={"loading.."} />;
    }
    const viewSiteIdPermitted = siteHasPermissions(
      acl,
      ["BACKOFFICE_SITE_SYSTEM_ID_VIEW"],
      createCompositeSiteId(site),
    );
    return (
      <Jumbotron
        header={site.name}
        subheader={(viewSiteIdPermitted && `${customerId}/${site.id}`) || null}
      />
    );
  }
}

interface IOwnProps {
  customerId: string;
  siteId: string;
}

export default connect(
  (state: GlobalState, ownProps: IOwnProps) => ({
    ...ownProps,
    user: state.user,
    customer: getCustomerById(state.customers, ownProps.customerId),
    site: getSiteById(state.sites, ownProps.customerId, ownProps.siteId),
    siteCreationWorkflows: getWorkflowsList(state.workflows).filter(
      ({ workflowType }: Workflow) => workflowType === "SITE_CREATION",
    ),
    currentSite: getCurrentSiteState(state),
    acl: state.acl,
  }),
  {
    pushRoute,
    ensureCustomer,
    ensureSite,
    loadWorkflows,
    fetchCurrentSite,
    ensureAcl,
    logToBws,
  },
)(DashboardContainer);
